/**
 * iter352 — Vue unifiée Agenda + Atelier (administration).
 * Planning du jour, trois colonnes de dossiers, charge des collaborateurs et
 * demandes à traiter — avec glisser-déposer (souris, tablette, doigt).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors,
  useDraggable, useDroppable, closestCorners,
} from "@dnd-kit/core";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  CalendarDays, Loader2, Clock, Users, FileQuestion, ChevronLeft, ChevronRight,
  GripVertical, CheckCircle2, CalendarClock, Plus,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const dateCourte = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CH", { day: "2-digit", month: "short" }) : "";

const COLONNES = [
  { key: "a_faire", label: "À faire", tone: "border-amber-200 bg-amber-50/40" },
  { key: "en_travaux", label: "En travaux", tone: "border-[#52a675]/30 bg-[#52a675]/5" },
  { key: "termine", label: "Terminé", tone: "border-gray-200 bg-gray-50" },
];

const ETAT_LABEL = { a_faire: "À faire", en_travaux: "En travaux", termine: "Terminé" };

const jourISO = (d) => d.toISOString().slice(0, 10);

const CarteOr = ({ or, onOpen, onAssigner }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `or:${or.id}`, data: { type: "or", or },
  });
  const timer = useRef(null);
  const longPress = useRef(false);
  const demarrer = () => {
    longPress.current = false;
    timer.current = setTimeout(() => { longPress.current = true; onAssigner(or); }, 550);
  };
  const arreter = () => clearTimeout(timer.current);
  return (
    <div
      ref={setNodeRef}
      onPointerDown={demarrer}
      onPointerUp={arreter}
      onPointerLeave={arreter}
      onContextMenu={(e) => { e.preventDefault(); onAssigner(or); }}
      className={`rounded-xl border-2 bg-white p-2.5 select-none ${isDragging ? "opacity-30" : ""} ${
        or.attente ? "border-orange-300" : "border-gray-200"
      }`}
      data-testid={`unifie-carte-${or.id}`}
    >
      <div className="flex items-start gap-1.5">
        <button
          {...attributes} {...listeners}
          className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none pt-0.5"
          data-testid={`unifie-poignee-${or.id}`}
          aria-label="Déplacer le dossier"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <button
          onClick={() => { if (!longPress.current) onOpen(or.id); }}
          className="flex-1 min-w-0 text-left"
          data-testid={`unifie-ouvrir-${or.id}`}
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-xs font-bold">{or.numero}</span>
            {or.non_lus > 0 && (
              <span className="min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                {or.non_lus}
              </span>
            )}
            {or.sous_statut && (
              <Badge
                variant="outline"
                className={`text-[9px] py-0 ${or.sous_statut.ton === "attente"
                  ? "bg-orange-50 text-orange-800 border-orange-300"
                  : "bg-[#52a675]/10 text-[#2d5f3f] border-[#52a675]/30"}`}
              >
                {or.sous_statut.label}
              </Badge>
            )}
          </div>
          <div className="text-xs text-gray-900 truncate">{or.vehicule_label || "Véhicule non renseigné"}</div>
          <div className="text-[11px] text-gray-500 truncate">{or.client_nom}</div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {or.assigne_a ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#52a675]/10 text-[#2d5f3f] font-medium">
                {or.assigne_a}
              </span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800">Non attribué</span>
            )}
            {(or.parcours || []).length > 1 && (
              <span className="text-[10px] text-gray-400 truncate" title={(or.parcours || []).join(" → ")}>
                {(or.parcours || []).join(" → ")}
              </span>
            )}
            {or.heures > 0 && (
              <span className="text-[10px] font-mono text-gray-500 ml-auto">{or.heures.toFixed(2)} h</span>
            )}
          </div>
        </button>
      </div>
    </div>
  );
};

const CarteRdv = ({ rdv }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `rdv:${rdv.id}`, data: { type: "rdv", rdv },
  });
  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-52 rounded-xl border-2 p-2.5 bg-white ${
        isDragging ? "opacity-30" : ""
      } ${rdv.or_id ? "border-[#52a675]/40" : "border-blue-200"}`}
      data-testid={`unifie-rdv-${rdv.id}`}
    >
      <div className="flex items-start gap-1.5">
        <button
          {...attributes} {...listeners}
          className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none pt-0.5"
          data-testid={`unifie-rdv-poignee-${rdv.id}`}
          aria-label="Déplacer le rendez-vous"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="min-w-0">
          <div className="font-mono text-xs font-bold">{rdv.heure_debut || "—"}</div>
          <div className="text-xs truncate">{rdv.vehicule_label || rdv.client_nom}</div>
          <div className="text-[11px] text-gray-500 truncate">{rdv.type_intervention_nom || rdv.client_nom}</div>
          {rdv.or_numero && (
            <Badge variant="outline" className="mt-1 text-[9px] py-0 bg-[#52a675]/10 border-[#52a675]/30 text-[#2d5f3f]">
              {rdv.or_numero}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

const Colonne = ({ col, cartes, onOpen, onAssigner }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${col.key}` });
  const [limite, setLimite] = useState(25);
  const visibles = cartes.slice(0, limite);
  return (
    <div
      ref={setNodeRef}
      className={`rounded-2xl border-2 p-2 min-h-[12rem] ${col.tone} ${isOver ? "ring-2 ring-[#52a675]" : ""}`}
      data-testid={`unifie-colonne-${col.key}`}
    >
      <div className="flex items-center justify-between px-1 pb-2">
        <span className="text-xs font-heading font-semibold uppercase tracking-wider text-gray-700">{col.label}</span>
        <span className="text-xs font-bold text-gray-500" data-testid={`unifie-count-${col.key}`}>{cartes.length}</span>
      </div>
      <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-0.5">
        {visibles.map((or) => <CarteOr key={or.id} or={or} onOpen={onOpen} onAssigner={onAssigner} />)}
        {cartes.length > limite && (
          <button
            onClick={() => setLimite(limite + 50)}
            className="w-full py-2 text-xs text-gray-500 hover:text-[#2d5f3f]"
            data-testid={`unifie-plus-${col.key}`}
          >
            Afficher {Math.min(50, cartes.length - limite)} dossiers de plus
          </button>
        )}
      </div>
    </div>
  );
};

export default function AtelierUnifie() {
  const navigate = useNavigate();
  const [date, setDate] = useState(jourISO(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actif, setActif] = useState(null);
  const [affectation, setAffectation] = useState(null);
  const [collegues, setCollegues] = useState([]);
  const [cible, setCible] = useState("");
  const [recherche, setRecherche] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/atelier/vue-unifiee", { params: { date } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/atelier/collegues").then(({ data }) => setCollegues(data.items || [])).catch(() => {});
  }, []);

  const changerEtat = async (or, etat) => {
    const principal = or.intervenants[0];
    if (!principal) {
      setAffectation({ or, etat });
      return;
    }
    try {
      await api.post(`/atelier/travail/${or.id}/etat`, { mecanicien_id: principal.mecanicien_id, etat });
      toast.success(`${or.numero} → ${ETAT_LABEL[etat]}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Déplacement impossible");
    }
  };

  const onDragEnd = async ({ active, over }) => {
    setActif(null);
    if (!over) return;
    const colKey = String(over.id).startsWith("col:") ? String(over.id).slice(4) : null;
    if (!colKey) return;
    const d = active.data.current;
    if (d.type === "rdv") {
      if (colKey !== "a_faire") { toast.info("Glisse le rendez-vous dans « À faire »"); return; }
      try {
        const { data: res } = await api.post(`/atelier/reception/${d.rdv.id}`, {});
        toast.success(res.cree ? `Dossier ${res.numero} créé` : `Dossier ${res.numero} ouvert`);
        load();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Réception impossible");
      }
      return;
    }
    if (d.type === "or") {
      const actuelle = COLONNES.find((c) => (data.colonnes[c.key] || []).some((x) => x.id === d.or.id));
      if (actuelle?.key === colKey) return;
      changerEtat(d.or, colKey);
    }
  };

  const confirmerAffectation = async () => {
    if (!cible) { toast.error("Choisis un collaborateur"); return; }
    const { or, etat } = affectation;
    try {
      await api.post(`/atelier/travail/${or.id}/affecter`, { mecanicien_id: cible });
      if (etat && etat !== "a_faire") {
        await api.post(`/atelier/travail/${or.id}/etat`, { mecanicien_id: cible, etat });
      }
      toast.success("Dossier attribué");
      setAffectation(null); setCible("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attribution impossible");
    }
  };

  const traiterDemande = async (dem) => {
    try {
      await api.post(`/demandes/${dem.id}/traiter`, { reponse: null });
      toast.success("Demande close, l'atelier est informé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible");
    }
  };

  const filtrer = useCallback((cartes) => {
    const q = recherche.trim().toLowerCase();
    if (!q) return cartes;
    return cartes.filter((c) => [c.numero, c.vehicule_label, c.client_nom, c.notes_client]
      .some((v) => (v || "").toLowerCase().includes(q)));
  }, [recherche]);

  const decaler = (n) => {    const d = new Date(date);
    d.setDate(d.getDate() + n);
    setDate(jourISO(d));
  };

  const dragOverlay = useMemo(() => {
    if (!actif) return null;
    const d = actif.data.current;
    const titre = d.type === "rdv" ? (d.rdv.vehicule_label || d.rdv.client_nom) : d.or.numero;
    return (
      <div className="rounded-xl border-2 border-[#52a675] bg-white px-3 py-2 text-xs font-semibold shadow-lg">
        {titre}
      </div>
    );
  }, [actif]);

  if (loading) {
    return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#52a675]" /></div>;
  }

  return (
    <DndContext
      sensors={sensors} collisionDetection={closestCorners}
      onDragStart={({ active }) => setActif(active)} onDragEnd={onDragEnd}
    >
      <div className="space-y-3" data-testid="atelier-unifie">
        {/* Planning du jour */}
        <Card className="p-3 border-gray-200" data-testid="unifie-planning">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="w-4 h-4 text-[#52a675]" />
            <span className="text-xs font-heading font-semibold uppercase tracking-wider text-gray-700">
              Rendez-vous du jour
            </span>
            <div className="ml-auto flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => decaler(-1)} data-testid="unifie-jour-prec">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Input
                type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="h-8 w-36 text-xs" data-testid="unifie-date"
              />
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => decaler(1)} data-testid="unifie-jour-suiv">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {data.rdv.length === 0 && (
              <div className="text-xs text-gray-400 py-3" data-testid="unifie-rdv-vide">Aucun rendez-vous ce jour-là</div>
            )}
            {data.rdv.map((r) => <CarteRdv key={r.id} rdv={r} />)}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            À l'arrivée du véhicule, glisse son rendez-vous dans « À faire » : le dossier s'ouvre ou se crée sans doublon.
          </p>
        </Card>

        {/* Colonnes atelier */}
        <div className="flex items-center gap-2">
          <Input
            value={recherche} onChange={(e) => setRecherche(e.target.value)}
            placeholder="Filtrer : numéro, plaque, client…"
            className="h-9 max-w-xs text-sm" data-testid="unifie-recherche"
          />
          {recherche && (
            <button onClick={() => setRecherche("")} className="text-xs text-gray-500 underline" data-testid="unifie-recherche-reset">
              effacer
            </button>
          )}
          {/* iter362 — Créer un dossier sans quitter la vue Atelier */}
          <Button
            onClick={() => navigate("/atelier/or/nouveau")}
            className="ml-auto h-9 bg-[#52a675] hover:bg-[#2d5f3f]"
            data-testid="unifie-nouvel-or"
          >
            <Plus className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Nouvel OR</span>
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {COLONNES.map((c) => (
            <Colonne
              key={c.key} col={c} cartes={filtrer(data.colonnes[c.key] || [])}
              onOpen={(id) => navigate(`/atelier/or/${id}`)}
              onAssigner={(or) => { setCible(""); setAffectation({ or, etat: null }); }}
            />
          ))}
        </div>

        {/* iter360 — Dossiers planifiés à une date future : rangés à part, retrouvables */}
        {(data.planifies || []).length > 0 && (
          <Card className="p-3 border-gray-200" data-testid="unifie-planifies">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="w-4 h-4 text-blue-500" />
              <span className="text-xs font-heading font-semibold uppercase tracking-wider text-gray-700">
                Planifiés plus tard
              </span>
              <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 text-[10px]">
                {data.planifies.length}
              </Badge>
            </div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {filtrer(data.planifies).map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/atelier/or/${p.id}`)}
                  className="w-full text-left p-2.5 rounded-xl border border-blue-100 bg-white hover:border-blue-300 transition-colors flex items-center gap-2"
                  data-testid={`unifie-planifie-${p.id}`}
                >
                  <span className="text-xs font-semibold text-blue-700 w-28 flex-shrink-0">
                    {dateCourte(p.rdv_planifie_date)} {p.rdv_planifie_heure || ""}
                  </span>
                  <span className="font-mono text-xs font-bold">{p.numero}</span>
                  <span className="text-xs text-gray-600 truncate flex-1">
                    {p.vehicule_label || p.client_nom}
                  </span>
                  {p.sous_statut && (
                    <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-600 border-gray-200">
                      {p.sous_statut.label}
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {/* Demandes à traiter */}
          <Card className="p-3 border-gray-200" data-testid="unifie-demandes">
            <div className="flex items-center gap-2 mb-2">
              <FileQuestion className="w-4 h-4 text-[#52a675]" />
              <span className="text-xs font-heading font-semibold uppercase tracking-wider text-gray-700">
                Demandes de l'atelier
              </span>
              {data.demandes.length > 0 && (
                <Badge variant="outline" className="bg-orange-50 text-orange-800 border-orange-300 text-[10px]">
                  {data.demandes.length}
                </Badge>
              )}
            </div>
            {data.demandes.length === 0 && (
              <div className="text-xs text-gray-400 py-2" data-testid="unifie-demandes-vide">Aucune demande en attente</div>
            )}
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {data.demandes.map((dem) => (
                <div key={dem.id} className="p-2.5 rounded-xl border border-orange-200 bg-orange-50/50" data-testid={`unifie-demande-${dem.id}`}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {dem.or && (
                      <button
                        onClick={() => navigate(`/atelier/or/${dem.or.id}`)}
                        className="font-mono text-xs font-bold underline"
                        data-testid={`unifie-demande-or-${dem.id}`}
                      >
                        {dem.or.numero}
                      </button>
                    )}
                    {dem.or?.vehicule_label && (
                      <span className="text-[11px] text-gray-600 truncate">{dem.or.vehicule_label}</span>
                    )}
                    <span className="text-[10px] text-gray-500 ml-auto">{dem.demandeur_nom}</span>
                  </div>
                  {dem.texte && <div className="text-xs text-gray-800 mt-1 whitespace-pre-wrap">{dem.texte}</div>}
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      onClick={() => navigate(`/atelier/or/${dem.or?.id || dem.or_id}`)}
                      className="text-[11px] text-[#2d5f3f] underline flex-1 text-left"
                      data-testid={`unifie-demande-repondre-${dem.id}`}
                    >
                      Répondre dans la conversation du dossier
                    </button>
                    <Button
                      variant="outline" onClick={() => traiterDemande(dem)}
                      className="h-8 text-xs border-gray-300 flex-shrink-0 bg-white"
                      data-testid={`unifie-demande-traiter-${dem.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Clore
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Charge des collaborateurs */}
          <Card className="p-3 border-gray-200" data-testid="unifie-charge">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[#52a675]" />
              <span className="text-xs font-heading font-semibold uppercase tracking-wider text-gray-700">
                Collaborateurs et heures
              </span>
            </div>
            {data.charge.length === 0 && <div className="text-xs text-gray-400 py-2">Aucune intervention en cours</div>}
            <div className="space-y-1.5">
              {data.charge.map((c) => (
                <div key={c.mecanicien_id} className="flex items-center gap-2 text-xs" data-testid={`unifie-charge-${c.mecanicien_id}`}>
                  <span className="font-medium text-gray-800 truncate flex-1">{c.nom}</span>
                  <span className="text-[10px] text-amber-700">{c.a_faire} à faire</span>
                  <span className="text-[10px] text-[#2d5f3f]">{c.en_travaux} en cours</span>
                  <span className="font-mono text-gray-600 inline-flex items-center gap-1 w-16 justify-end">
                    <Clock className="w-3 h-3" />{c.heures.toFixed(2)} h
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <DragOverlay>{dragOverlay}</DragOverlay>

      <Dialog open={!!affectation} onOpenChange={(v) => !v && setAffectation(null)}>
        <DialogContent className="max-w-sm" data-testid="unifie-affecter-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Assigner le dossier</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500">
            {affectation?.or?.numero} — {affectation?.or?.vehicule_label}. Choisis qui s'en occupe.
          </p>
          <Select value={cible} onValueChange={setCible}>
            <SelectTrigger className="h-11" data-testid="unifie-affecter-select">
              <SelectValue placeholder="Collaborateur" />
            </SelectTrigger>
            <SelectContent>
              {collegues.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`unifie-affecter-${c.id}`}>{c.nom}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAffectation(null)} data-testid="unifie-affecter-annuler">Annuler</Button>
            <Button onClick={confirmerAffectation} className="bg-[#52a675] hover:bg-[#2d5f3f]" data-testid="unifie-affecter-valider">
              Attribuer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DndContext>
  );
}
